import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Heart, Sparkles, Star } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function GratitudeCelebrationPractices() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const practices = [
    {
      title: 'Morning Gratitude Reflection',
      description: 'Before starting your day, take 2-3 minutes to identify three things you\'re grateful for. Connect each gratitude to a need that was met.',
      example: '"I\'m grateful for my morning coffee because it meets my need for comfort and self-care."',
      icon: '🌅'
    },
    {
      title: 'Gratitude Journaling (NVC Style)',
      description: 'Write about something you\'re grateful for using the full OFNR format.',
      example: '"When I noticed [observation], I felt [feeling] because my need for [need] was met. I request of myself [request] to continue nurturing this."',
      icon: '📝'
    },
    {
      title: 'Celebration Letter',
      description: 'Write a letter to someone expressing what they did, how you felt, and what needs were met.',
      example: '"Dear [Name], When you [action], I felt [feeling] because my need for [need] was fulfilled. Thank you for contributing to my life in this way."',
      icon: '💌'
    },
    {
      title: 'Evening Appreciation Practice',
      description: 'At day\'s end, share with a partner or journal about three celebrations from your day.',
      example: 'Focus on moments where needs were met—both your own and others\'. Notice the feelings that arise as you recall these moments.',
      icon: '🌙'
    },
    {
      title: 'Appreciating Yourself',
      description: 'Practice self-celebration by acknowledging your own contributions to your well-being and others\'.',
      example: '"When I [action I took], I contributed to [person\'s] need for [need], and this meets my need for [contribution/meaning/etc.]."',
      icon: '🪞'
    },
    {
      title: 'The Three-Breath Celebration',
      description: 'A quick practice: Breath 1 - recall something to celebrate. Breath 2 - feel the feelings. Breath 3 - connect to the met need.',
      example: 'Use this throughout the day whenever you notice something positive, no matter how small.',
      icon: '🌬️'
    }
  ];

  const celebrationPhrases = [
    'When I see what you did, I feel touched because my need for kindness was met.',
    'I\'m celebrating that my need for connection was met when we talked.',
    'Thank you for meeting my need for support—I feel relieved and grateful.',
    'When you listened without judgment, you met my need for acceptance.',
    'I\'m deeply appreciative because my need for understanding was met.',
    'Your presence contributed to my need for companionship—thank you.'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Gratitude and Celebration Practices</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 40</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Sparkles className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Gratitude and Celebration Practices (NVC Style)</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">In NVC, gratitude is expressed by connecting appreciation to the specific needs that were met. This creates deeper connection than generic "thank you."</p>
          </div>

          <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-amber-200 mb-3 flex items-center gap-2">
              <Heart className="h-5 w-5" /> The NVC Celebration Formula
            </h3>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <p className="text-white text-lg font-medium mb-2">"When you [observation], I feel [feeling] because my need for [need] was met."</p>
              <p className="text-slate-300 text-sm">This format connects behavior to universal needs, creating meaningful acknowledgment.</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mb-6">Daily Practices</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {practices.map((practice, index) => (
              <div key={index} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6 hover:border-amber-600/50 transition-all">
                <div className="text-4xl mb-3">{practice.icon}</div>
                <h4 className="text-lg font-bold text-amber-400 mb-2">{practice.title}</h4>
                <p className="text-slate-300 mb-3">{practice.description}</p>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-slate-400 text-sm italic">{practice.example}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-purple-200 mb-4 flex items-center gap-2">
              <Star className="h-5 w-5" /> Sample Celebration Phrases
            </h3>
            <div className="space-y-3">
              {celebrationPhrases.map((phrase, index) => (
                <div key={index} className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-purple-100">• {phrase}</p>
                </div>
              ))}
            </div>
          </div>

          <AIPracticeScenario pageTitle="Gratitude and Celebration Practices" pageNumber={40} />
          <PageMessaging pageTitle="Gratitude and Celebration Practices" pageNumber={40} />
        </div>
      </div>
    </div>
  );
}
